import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, AlertTriangle, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';

const VolvoService = () => {
  const volvoFeatures = [
    'VIDA Diagnostic System',
    'Swedish Engineering Experts',
    'Genuine Volvo Parts',
    'All Volvo Models',
    '12-Month/12k Mile Warranty',
    'Dallas Climate Specialists'
  ];

  const engineServices = [
    'T5/T6/T8 Twin Engine performance optimization',
    'Direct injection system cleaning and service',
    'Turbocharger & supercharger diagnostics and repair',
    'Cooling system maintenance and overheating resolution',
    'Engine timing belt and chain replacement',
    'Oil leak diagnosis and repair (common in aging Volvos)'
  ];

  const electricalServices = [
    'Sensus infotainment system troubleshooting',
    'Battery and charging system testing and replacement',
    'Advanced safety system calibration and repair',
    'Complex computer module programming and updates',
    'CEM (Central Electronic Module) diagnostics',
    'Dashboard instrument cluster repairs'
  ];

  const transmissionServices = [
    'Aisin Warner automatic transmission fluid service',
    'Geartronic transmission diagnosis and repair',
    'Transmission control module diagnostics and programming',
    'Complete transmission rebuild and replacement',
    'AWD system diagnosis and service',
    'Driveshaft and CV joint replacement'
  ];

  const brakeServices = [
    'High-performance brake pad replacement',
    'DOT-compliant brake fluid flush and replacement',
    'Precision rotor machining and replacement',
    'Electronic parking brake calibration and repair',
    'ABS module diagnosis and repair',
    'Complete brake system inspections'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>Specialized Volvo Repair & Service in Dallas | Paradise-Performance</title>
        <meta name="description" content="Specialized Volvo repair and maintenance for Dallas drivers. European auto experts deliver dealer-level service at competitive prices. Save 30-40% vs dealership. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair/volvo" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair/volvo" />
        <meta property="og:title" content="Volvo Repair Dallas | Swedish Auto Specialists" />
        <meta property="og:description" content="Dallas Volvo specialists. VIDA diagnostics, XC90, XC60, S60 experts. 30-40% less than dealership. Call (817) 382-2316." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/volvo-repair.webp" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Volvo Repair Dallas | Swedish Auto Specialists" />
        <meta name="twitter:description" content="Dallas Volvo specialists. VIDA diagnostics, XC90, XC60, S60 experts. 30-40% less than dealership." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/volvo-repair.webp" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - Volvo Repair Dallas",
            "url": "https://paradise-performance.com/services/european-car-repair/volvo",
            "telephone": "+18173822316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": ["Dallas", "Fort Worth", "Plano"],
            "priceRange": "$$"
          })}
        </script>

        {/* FAQ Schema for Featured Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does Volvo maintenance cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Volvo maintenance costs average $769 to $1,100 per year depending on the model. An oil change runs $100-$140 at an independent shop (vs $150-$220 at dealerships). Timing belt replacement costs $800-$1,200. At Paradise-Performance near Dallas, we're typically 30-40% less than Volvo dealership prices."
                }
              },
              {
                "@type": "Question",
                "name": "Are Volvos expensive to maintain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Volvos are moderately expensive to maintain—more than mainstream brands but less than BMW or Mercedes. Annual costs average $769-$1,100. Major repairs like timing belt/chain service ($800-$1,500) and transmission work ($1,500-$3,500) can be costly. Using an independent Volvo specialist saves 30-40% vs dealership prices."
                }
              },
              {
                "@type": "Question",
                "name": "What are common Volvo problems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Common Volvo issues include: CEM (Central Electronic Module) failures, oil leaks (PCV system, turbo oil lines), transmission problems (older models), failing fuel pumps, air conditioning compressor failures, and suspension wear. Newer T5/T6 engines may have oil consumption issues. Regular maintenance prevents most problems."
                }
              },
              {
                "@type": "Question",
                "name": "How often should I service my Volvo?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Volvos should be serviced every 10,000 miles or 12 months. Key intervals: oil change every 10,000 miles (7,500 for turbo models in Texas heat), timing belt every 105,000 miles (5-cylinder) or timing chain inspection at 100,000 miles (newer engines), brake fluid every 2 years, and transmission fluid every 50,000 miles."
                }
              },
              {
                "@type": "Question",
                "name": "Is Volvo XC90 reliable?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Volvo XC90 is generally reliable, especially the 2016+ second generation. Common issues include air suspension problems (if equipped), infotainment glitches, and minor electrical issues. The T6 twin-charged engine is robust when maintained properly. Regular service and addressing issues early keeps XC90s running well past 200,000 miles."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Volvo"
        brandColor="#003057"
        tagline="Specialized Volvo Repair & Service in Dallas"
        description="Swedish Engineering Excellence Deserves Expert Care"
        features={volvoFeatures}
        heroImage="/assets/assets/images/volvo-repair.webp"
        heroImageAlt="Volvo Service Center"
        heroVariant="default"
      />

      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Intro */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Dallas' Leading Volvo Specialists | European Auto Repair Experts</h2>
            
            <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
              <div>
                <p className="text-lg text-gray-700 mb-4">
                  Studies show that 94% of Volvo vehicles can surpass 200,000 miles when properly maintained by qualified specialists. 
                  At Paradise-Performance, our European auto repair technicians understand that your Volvo represents Scandinavian 
                  engineering excellence and uncompromising safety standards that require specialized knowledge.
                </p>
                <p className="text-lg text-gray-700">
                  While Dallas has many general repair shops, few truly understand Volvo's unique engineering philosophy. 
                  Our expert Volvo technicians provide the specialized service your vehicle deserves. From routine maintenance 
                  to complex electrical diagnostics, we deliver dealer-level expertise without the inflated dealer-level prices 
                  that Dallas Volvo owners often face.
                </p>
              </div>
              <div className="relative h-80 rounded-lg overflow-hidden">
                <img 
                  src="/assets/assets/images/new/euro-repair-img/bmw-turbo-leak.webp"
                  alt="Volvo Diagnostic Testing"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{clipPath: 'circle(134px)', transform: 'scale(1.5)'}}
                />
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-[#003057] p-6 rounded">
              <p className="text-gray-800">
                <strong>Is your Volvo's check engine light illuminated? Experiencing unusual vibrations or noise? 
                Or is it simply time for scheduled maintenance?</strong> Our specialized European auto repair facility in 
                Paradise, TX serving Dallas has the expertise and equipment to keep your Volvo running at its best. 
                We understand the precise requirements of Swedish engineering that many general repair shops simply 
                don't have the training to address.
              </p>
            </div>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <motion.div {...fadeInUp} className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-blue-600">
              <Settings className="w-10 h-10 text-blue-600 mb-3" />
              <h3 className="text-xl font-bold mb-3">Engine Service</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• T5/T6/T8 specialists</li>
                <li>• Turbo/supercharger service</li>
                <li>• Timing belt replacement</li>
                <li>• Oil leak repair</li>
              </ul>
            </motion.div>

            <motion.div {...fadeInUp} className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-blue-600">
              <Wrench className="w-10 h-10 text-blue-600 mb-3" />
              <h3 className="text-xl font-bold mb-3">Transmission</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• Aisin Warner service</li>
                <li>• Geartronic repair</li>
                <li>• AWD system service</li>
                <li>• CV joint replacement</li>
              </ul>
            </motion.div>

            <motion.div {...fadeInUp} className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-blue-600">
              <Shield className="w-10 h-10 text-blue-600 mb-3" />
              <h3 className="text-xl font-bold mb-3">Safety Systems</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• City Safety calibration</li>
                <li>• BLIS service</li>
                <li>• Pilot Assist repair</li>
                <li>• ABS module repair</li>
              </ul>
            </motion.div>

            <motion.div {...fadeInUp} className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-blue-600">
              <Car className="w-10 h-10 text-blue-600 mb-3" />
              <h3 className="text-xl font-bold mb-3">Electrical</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• CEM diagnostics</li>
                <li>• Sensus repair</li>
                <li>• Module programming</li>
                <li>• Battery service</li>
              </ul>
            </motion.div>
          </div>

          {/* Common Issues */}
          <motion.div {...fadeInUp} className="bg-white rounded-xl p-8 shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Common Volvo Issues in Dallas</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border-l-4 border-yellow-500 pl-6">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-500 mr-2" />
                  Cooling System Failures
                </h3>
                <p className="text-gray-700 mb-2">Dallas heat accelerates wear:</p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Water pump failures (S60/XC90)</li>
                  <li>• Thermostat malfunctions</li>
                  <li>• Expansion tank cracks</li>
                </ul>
                <p className="mt-3 text-sm font-semibold text-green-600">
                  We recommend bi-annual cooling checks
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 pl-6">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-500 mr-2" />
                  Transmission Issues
                </h3>
                <p className="text-gray-700 mb-2">Traffic strains transmissions:</p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Hard shifts (2010-2015)</li>
                  <li>• Fluid overheating</li>
                  <li>• Valve body wear</li>
                </ul>
                <p className="mt-3 text-sm font-semibold text-green-600">
                  Fluid service every 50k miles recommended
                </p>
              </div>
            </div>
          </motion.div>

          {/* Model Coverage */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">All Volvo Models Serviced</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-bold text-blue-900 mb-2">Sedans</h3>
                <p className="text-gray-600">S60, S90</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-bold text-blue-900 mb-2">SUVs</h3>
                <p className="text-gray-600">XC40, XC60, XC90</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-bold text-blue-900 mb-2">Wagons</h3>
                <p className="text-gray-600">V60, V90, V90 Cross Country</p>
              </div>
            </div>

            {/* Safety Features */}
            <div className="mt-8 bg-blue-900 rounded-xl p-6 text-white">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Heart className="w-6 h-6 mr-2" />
                Advanced Safety Systems We Service
              </h3>
              <div className="flex flex-wrap gap-2">
                {['City Safety', 'Pilot Assist', 'BLIS', 'IntelliSafe', 'Run-off Road Protection'].map((feature, i) => (
                  <span key={i} className="bg-white/20 px-3 py-1 rounded-full text-sm">{feature}</span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Additional Image Gallery */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="relative h-80 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/euro-repair-img/bmw-tensioner-pulleys.webp"
                  alt="Volvo engine service"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-bold">Swedish Engineering Excellence</p>
                  <p className="text-white/80 text-sm">Expert Volvo engine maintenance</p>
                </div>
              </div>
              <div className="relative h-80 overflow-hidden rounded-lg shadow-xl">
                <img 
                  src="/assets/assets/images/new/brakes/brake-system.webp"
                  alt="Volvo brake system service"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-bold">Advanced Safety Systems</p>
                  <p className="text-white/80 text-sm">Brake and safety system specialists</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Comparison */}
          <motion.div {...fadeInUp} className="bg-white rounded-xl p-8 shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Why Choose Paradise-Performance?</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-blue-900 text-white">
                    <th className="p-3 text-left">Service Factor</th>
                    <th className="p-3 text-center">Dallas Dealer</th>
                    <th className="p-3 text-center">Generic Shop</th>
                    <th className="p-3 text-center">Paradise-Performance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3 font-semibold">VIDA Diagnostics</td>
                    <td className="p-3 text-center">Yes</td>
                    <td className="p-3 text-center">No</td>
                    <td className="p-3 text-center text-green-600 font-bold">Yes</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3 font-semibold">Wait Time</td>
                    <td className="p-3 text-center">1-2 weeks</td>
                    <td className="p-3 text-center">Varies</td>
                    <td className="p-3 text-center text-green-600 font-bold">1-3 days</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-semibold">Warranty</td>
                    <td className="p-3 text-center">Manufacturer</td>
                    <td className="p-3 text-center">30 days</td>
                    <td className="p-3 text-center text-green-600 font-bold">12 months</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div {...fadeInUp} className="text-center bg-gray-900 text-white rounded-xl p-8">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Volvo Service</h2>
            <p className="text-xl mb-6 text-gray-300">Expert Swedish auto repair in Dallas</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8173822316" className="inline-block px-8 py-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors">
                Call (817) 382-2316
              </a>
              <Link to="/contact" className="inline-block px-8 py-4 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-colors">
                Schedule Online
              </Link>
            </div>
          </motion.div>

          {/* Related Services */}
          <div className="mt-12 text-center">
            <p className="text-gray-600">
              Also servicing: {' '}
              <Link to="/services/european-car-repair/bmw" className="text-blue-600 hover:underline">BMW</Link>, {' '}
              <Link to="/services/european-car-repair/mercedes" className="text-blue-600 hover:underline">Mercedes</Link>, {' '}
              <Link to="/services/european-car-repair/audi" className="text-blue-600 hover:underline">Audi</Link>, {' '}
              and all <Link to="/services/european-car-repair" className="text-blue-600 hover:underline">European auto repair Dallas</Link> services
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default VolvoService;
